import BaseProductTier from 'developer-docs-mdx-components/components/ProductTier/BaseProductTier';
import ClickableHeader from 'developer-docs-shared-components/modules/Header/ClickableHeader';
import RelatedDocs from 'developer-docs-shared-components/components/RelatedDocs';
import { MONOLITH, OLAF } from 'HubStyleTokens/colors';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { MOBILE_MENU_BREAKPOINT, mediaQueries, viewportSizes } from 'developer-docs-shared-components/utils/mediaQueries';
import { useMemo } from 'react';
import styled, { css } from 'styled-components';
import { introductionId, useCasesId } from './constants';
import { publicApiSpecProductTierRequirementsToProductTierProps } from './util';
import { CARD_BORDER_RADIUS, CARD_BOX_SHADOW, INNER_CARD_PADDING, NEXT_COMPONENT_SPACING } from 'developer-docs-shared-components/config/cssConstants';
import { isUrlLocalized } from '../../utils/localization';
import { getEnglishBaseUrl } from '../../utils/urls';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
/**
 * The breakpoint below which left and right columns become stacked. Specific
 * to this component.
 */
const COLUMN_BREAKPOINT = viewportSizes.desktop - 200;
const sharedHeadingStyles = css(["color:", ";font-weight:500;"], MONOLITH);

/**
 * A semantic <h2 />. Unlike the `UIComponents` heading, does not add an
 * unnecessary `aria-level`. Still uses and extends `UIComponents` base heading
 * styles.
 *
 * TODO: should this H2 become a shared component within
 * developer-docs-shared-components too?
 *
 * @see https://uxplanet.org/should-i-use-aria-level-in-the-h-tag-8efc2cd0a114
 * @see https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-level#with_heading_role
 */
const H2 = styled.h2.withConfig({
  displayName: "APIHeaderCard__H2",
  componentId: "s1germ-0"
})(["", ""], sharedHeadingStyles);

/**
 * A semantic <h3 />. Unlike the `UIComponents` heading, does not add an
 * unnecessary `aria-level`. Still uses and extends `UIComponents` base heading
 * styles.
 *
 * TODO: should this H3 become a shared component within
 * developer-docs-shared-components too?
 *
 * @see https://uxplanet.org/should-i-use-aria-level-in-the-h-tag-8efc2cd0a114
 * @see https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-level#with_heading_role
 */
const H3 = styled.h3.withConfig({
  displayName: "APIHeaderCard__H3",
  componentId: "s1germ-1"
})(["", " font-size:22px;"], sharedHeadingStyles);
const CardParagraph = styled.p.withConfig({
  displayName: "APIHeaderCard__CardParagraph",
  componentId: "s1germ-2"
})(["margin:16px 0 32px 0;line-height:1.65;"]);
const Card = styled.section.withConfig({
  displayName: "APIHeaderCard__Card",
  componentId: "s1germ-3"
})(["margin:16px 0;padding:", "px;background-color:", ";border-radius:", "px;box-shadow:", ";", "{display:flex;justify-content:space-between;margin-top:", "px;margin-bottom:", "px;}"], INNER_CARD_PADDING, OLAF, CARD_BORDER_RADIUS, CARD_BOX_SHADOW, mediaQueries.aboveWidth(COLUMN_BREAKPOINT), NEXT_COMPONENT_SPACING, NEXT_COMPONENT_SPACING);

/**
 * Shown to the left at desktop. Houses the intro and use cases for this API.
 */
const LeftColumn = styled.div.withConfig({
  displayName: "APIHeaderCard__LeftColumn",
  componentId: "s1germ-4"
})(["max-width:800px;", "{width:60%;}"], mediaQueries.aboveWidth(COLUMN_BREAKPOINT));

/**
 * Shown to the right at desktop; Houses the applicable products (Product Tier)
 * or minimum requirements for using this API.
 */
const RightColumn = styled.aside.withConfig({
  displayName: "APIHeaderCard__RightColumn",
  componentId: "s1germ-5"
})([".card-wrapper{max-width:100%;}", "{width:30.7%;}", "{margin-top:50px;}"], mediaQueries.aboveWidth(COLUMN_BREAKPOINT), mediaQueries.betweenWidths(MOBILE_MENU_BREAKPOINT, COLUMN_BREAKPOINT));
const APIHeaderCard = ({
  nonLegacyIntroContent,
  legacyIntroContent
}) => {
  const intro = useMemo(() => (nonLegacyIntroContent === null || nonLegacyIntroContent === void 0 ? void 0 : nonLegacyIntroContent.intro) || (legacyIntroContent === null || legacyIntroContent === void 0 ? void 0 : legacyIntroContent.intro), [legacyIntroContent, nonLegacyIntroContent]);
  const useCase = useMemo(() => (nonLegacyIntroContent === null || nonLegacyIntroContent === void 0 ? void 0 : nonLegacyIntroContent.useCase) || (legacyIntroContent === null || legacyIntroContent === void 0 ? void 0 : legacyIntroContent.useCase), [legacyIntroContent, nonLegacyIntroContent]);
  const sharedLinkProps = useMemo(() => {
    return {
      localization: {
        isUrlLocalized,
        getEnglishBaseUrl
      },
      relatedDocType: 'guide'
    };
  }, []);
  const publicApiSpecProductTierRequirements = useMemo(() => {
    return (nonLegacyIntroContent === null || nonLegacyIntroContent === void 0 ? void 0 : nonLegacyIntroContent.requirements) && publicApiSpecProductTierRequirementsToProductTierProps(nonLegacyIntroContent.requirements);
  }, [nonLegacyIntroContent]);
  if (!(intro || useCase)) {
    return null;
  }
  return /*#__PURE__*/_jsxs(Card, {
    children: [/*#__PURE__*/_jsxs(LeftColumn, {
      children: [intro && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(ClickableHeader, {
          Component: H2,
          id: introductionId,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "developer-docs-ui.apiReference.introductionTitle"
          })
        }), /*#__PURE__*/_jsx(CardParagraph, {
          children: intro
        })]
      }), useCase && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(ClickableHeader, {
          Component: H3,
          id: useCasesId,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "developer-docs-ui.apiReference.useCasesTitle"
          })
        }), /*#__PURE__*/_jsx(CardParagraph, {
          children: useCase
        })]
      }), nonLegacyIntroContent !== null && nonLegacyIntroContent !== void 0 && nonLegacyIntroContent.relatedDocumentation &&
      // currently only support showing one (the first) related documentation
      nonLegacyIntroContent.relatedDocumentation[0] ? /*#__PURE__*/_jsx(RelatedDocs, Object.assign({
        href: nonLegacyIntroContent.relatedDocumentation[0].url,
        title: nonLegacyIntroContent.relatedDocumentation[0].name
      }, sharedLinkProps)) : legacyIntroContent && legacyIntroContent.relatedDocuments && /*#__PURE__*/_jsx(RelatedDocs, Object.assign({
        title: legacyIntroContent.relatedDocuments.title,
        href: legacyIntroContent.relatedDocuments.href
      }, sharedLinkProps))]
    }), publicApiSpecProductTierRequirements && publicApiSpecProductTierRequirements.length ? /*#__PURE__*/_jsx(RightColumn
    // Since page has multiple <aside>s, label them: https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/complementary_role#labeling_landmarks
    , {
      "aria-label": I18n.text('developer-docs-mdx-components.productTier.header'),
      children: /*#__PURE__*/_jsx(BaseProductTier, {
        tiers: publicApiSpecProductTierRequirements
      })
    }) : legacyIntroContent && legacyIntroContent.requiredProductTiers && /*#__PURE__*/_jsx(RightColumn, {
      children: /*#__PURE__*/_jsx(BaseProductTier, {
        tiers: legacyIntroContent.requiredProductTiers
      })
    })]
  });
};
export default APIHeaderCard;